import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  User,
  Globe,
  Check,
  Sparkles,
  Eye,
  ChevronDown,
} from "lucide-react";
import { saveUserSettings, UserSettings } from "@/lib/userSettings";
import { Helmet } from "react-helmet";
import appLogo from "@/assets/eyecontrol-icon-white.png";
import { 
  useCaregiverTranslation, 
  getCaregiverLanguage, 
  setCaregiverLanguage,
  type CaregiverLanguage 
} from "@/hooks/useCaregiverTranslation";

// Simplified flow: language -> control_config (gesture selection) -> action_mapping -> complete
// Default control method is always eye_control - users can change in settings later
const STEPS = ["welcome", "name", "gender", "language", "control_config", "action_mapping", "complete"] as const;
type Step = (typeof STEPS)[number];

// Note: Control methods selection removed from onboarding
// Default is eye_control - users can change in Settings later

const LANGUAGES = [
  { code: "he", name: "עברית", flag: "🇮🇱" },
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "ar", name: "العربية", flag: "🇸🇦" },
  { code: "ru", name: "Русский", flag: "🇷🇺" },
];

// Eye control gesture definitions
type EyeGestureId = "look_up" | "look_down" | "look_left" | "look_right" | "blink";
const EYE_GESTURES: EyeGestureId[] = ["look_up", "look_down", "look_left", "look_right", "blink"];
const eyeGestureIcons: Record<EyeGestureId, React.ElementType> = {
  look_up: ArrowUp,
  look_down: ArrowDown,
  look_left: ArrowLeft,
  look_right: ArrowRight,
  blink: Eye,
};

// Note: Mouth gesture definitions removed - only eye control is used in simplified onboarding

export default function Onboarding() {
  const navigate = useNavigate();
  const { t, lang: caregiverLang, isRTL, dir } = useCaregiverTranslation();
  const [currentStep, setCurrentStep] = useState<Step>("welcome");
  const [caregiverLanguage, setCaregiverLangState] = useState<CaregiverLanguage>(getCaregiverLanguage());

  // Allow scrolling on onboarding page (global overflow-hidden is set on html/body/#root)
  useEffect(() => {
    const root = document.getElementById('root');
    document.documentElement.style.overflowY = 'auto';
    document.body.style.overflowY = 'auto';
    if (root) root.style.overflowY = 'auto';
    return () => {
      document.documentElement.style.overflowY = '';
      document.body.style.overflowY = '';
      if (root) root.style.overflowY = '';
    };
  }, []);
  const [settings, setSettings] = useState<Partial<UserSettings>>({
    name: "",
    gender: "neutral" as const,
    language: "he",
    controlMethod: "eye_control", // Default is always eye_control
    // Control method specific settings
    eyeControlGestures: [],
    mouthGestures: [],
    externalSwitchCount: 1,
    scanMode: "cell_by_cell",
    scanSpeed: 3.0,
    scanControlMode: "automatic",
    externalSwitchScanType: "automatic",
    actionMapping: {},
  });
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  
  // Update caregiver language and force re-render
  const handleCaregiverLanguageChange = (lang: CaregiverLanguage) => {
    setCaregiverLanguage(lang);
    setCaregiverLangState(lang);
  };

  const GENDERS = [
    { value: "female", label: t("genderFemale"), description: t("genderFemaleDesc") },
    { value: "male", label: t("genderMale"), description: t("genderMaleDesc") },
    { value: "neutral", label: t("genderNeutral"), description: t("genderNeutralDesc") },
  ] as const;

  const stepIndex = STEPS.indexOf(currentStep);
  const isFirstStep = stepIndex === 0;
  const isLastStep = currentStep === "complete";
  const isFemale = settings.gender === "female";


  const scrollToTop = () => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const root = document.getElementById('root');
    if (root) root.scrollTop = 0;
  };

  const goNext = () => {
    const nextIndex = stepIndex + 1;
    if (nextIndex < STEPS.length) {
      const nextStep = STEPS[nextIndex];
      // Skip action_mapping if no gestures selected (will use auto-scan)
      if (nextStep === "action_mapping" && (settings.eyeControlGestures || []).length < 2) {
        setCurrentStep(STEPS[nextIndex + 1]); // Skip mapping, go to complete
      } else {
        setCurrentStep(nextStep);
      }
      scrollToTop();
    }
  };

  const goBack = () => {
    const prevIndex = stepIndex - 1;
    if (prevIndex >= 0) {
      const prevStep = STEPS[prevIndex];
      // Skip action_mapping going back if not enough gestures
      if (prevStep === "action_mapping" && (settings.eyeControlGestures || []).length < 2) {
        setCurrentStep(STEPS[prevIndex - 1]); // Skip mapping
      } else {
        setCurrentStep(prevStep);
      }
      scrollToTop();
    }
  };

  const handleComplete = () => {
    saveUserSettings({
      ...settings,
      onboardingComplete: true,
    });
    navigate("/");
  };

  const updateSetting = <K extends keyof UserSettings>(
    key: K,
    value: UserSettings[K]
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  // Toggle gesture selection
  const toggleEyeGesture = (gestureId: EyeGestureId) => {
    const current = settings.eyeControlGestures || [];
    const newGestures = current.includes(gestureId)
      ? current.filter((g) => g !== gestureId)
      : [...current, gestureId];

    // Auto-set scan control mode based on gesture count:
    // 4+ gestures = gesture_controlled, 3 or less = automatic
    const newScanControlMode = newGestures.length >= 4 ? "gesture_controlled" : "automatic";

    // cell_by_cell is only available with 5 gestures + gesture_controlled.
    // Otherwise force row_and_column (matches EyeControlSettings behavior).
    const cellByCellAvailable =
      newGestures.length === 5 && newScanControlMode === "gesture_controlled";
    const newScanMode =
      !cellByCellAvailable && (settings.scanMode || "cell_by_cell") === "cell_by_cell"
        ? "row_and_column"
        : settings.scanMode;

    setSettings(prev => ({
      ...prev,
      eyeControlGestures: newGestures,
      scanControlMode: newScanControlMode,
      scanMode: newScanMode,
    }));
  };


  // Get scanning mode description based on selected count (used in eye control config)
  const getGestureCountDescription = (count: number) => {
    const descKeys: Record<number, string> = {
      1: "gesture1Desc",
      2: "gesture2Desc",
      3: "gesture3Desc",
      4: "switch4Desc",
      5: "switch5Desc",
    };
    return descKeys[count] ? t(descKeys[count] as any) : "";
  };

  // Check if control_config can continue - only eye control is used in onboarding
  const canContinueControlConfig = () => {
    return (settings.eyeControlGestures || []).length > 0;
  };

  // Action mapping helpers for eye control
  const getGestureOptions = () => [
    { id: "blink", label: t("blinkGesture"), shortLabel: t("blinkGestureShort") },
    { id: "look_up", label: t("lookUpGesture"), shortLabel: t("lookUpGestureShort") },
    { id: "look_down", label: t("lookDownGesture"), shortLabel: t("lookDownGestureShort") },
    { id: "look_left", label: t("lookLeftGesture"), shortLabel: t("lookLeftGestureShort") },
    { id: "look_right", label: t("lookRightGesture"), shortLabel: t("lookRightGestureShort") },
  ];

  const getActionsForGestureCount = (count: number) => {
    if (count >= 5) return [
      { id: "select", label: t("selectEnter"), defaultGesture: "blink" },
      { id: "up", label: t("upAction"), defaultGesture: "look_up" },
      { id: "down", label: t("downAction"), defaultGesture: "look_down" },
      { id: "left", label: t("leftAction"), defaultGesture: "look_left" },
      { id: "right", label: t("rightAction"), defaultGesture: "look_right" },
    ];
    if (count === 4) return [
      { id: "select", label: t("selectEnter"), defaultGesture: "blink" },
      { id: "next", label: t("nextAction"), defaultGesture: "look_right" },
      { id: "previous", label: t("previousAction"), defaultGesture: "look_left" },
      { id: "back", label: t("backAction"), defaultGesture: "look_down" },
    ];
    if (count === 3) return [
      { id: "select", label: t("selectEnter"), defaultGesture: "blink" },
      { id: "next", label: t("nextAction"), defaultGesture: "look_right" },
      { id: "back", label: t("backAction"), defaultGesture: "look_left" },
    ];
    if (count === 2) return [
      { id: "select", label: t("selectEnter"), defaultGesture: "blink" },
      { id: "back", label: t("backAction"), defaultGesture: "look_down" },
    ];
    return [
      { id: "select", label: t("selectEnter"), defaultGesture: "blink" },
    ];
  };

  const getScanModeInfo = (gestureCount: number) => {
    if (gestureCount >= 5) return { title: t("fullDirectionalNav"), subtitle: t("fullDirectionalNavDesc") };
    if (gestureCount === 4) return { title: t("advancedNav"), subtitle: t("advancedNavDesc") };
    if (gestureCount === 3) return { title: t("manualScanNav"), subtitle: t("manualScanNavDesc") };
    if (gestureCount === 2) return { title: t("autoScanWithCancel"), subtitle: t("autoScanWithCancelDesc") };
    return { title: t("autoScanOnly"), subtitle: t("autoScanOnlyDesc") };
  };

  const getActionIcon = (actionId: string) => {
    switch (actionId) {
      case "up": return ArrowUp;
      case "down": return ArrowDown;
      case "left":
      case "previous":
      case "back": return ArrowLeft;
      case "right":
      case "next": return ArrowRight;
      case "select":
      case "cancel": return Check;
      default: return ArrowLeft;
    }
  };

  // Initialize action mapping when entering the action_mapping step
  const initializeActionMapping = () => {
    const gestures = settings.eyeControlGestures || [];
    const actions = getActionsForGestureCount(gestures.length);
    const currentMapping = settings.actionMapping || {};
    
    // Check that current mapping is valid AND covers ALL required actions
    const isValid = Object.values(currentMapping).every((g) => gestures.includes(g as string));
    const coversAllActions = actions.every(
      (a) => currentMapping[a.id] && gestures.includes(currentMapping[a.id] as string)
    );
    if (isValid && coversAllActions) {
      return; // Keep existing valid mapping
    }

    // Create default mapping
    const defaultMapping: Record<string, string> = {};
    const usedGestures: string[] = [];
    
    actions.forEach((action, index) => {
      if (gestures.includes(action.defaultGesture) && !usedGestures.includes(action.defaultGesture)) {
        defaultMapping[action.id] = action.defaultGesture;
        usedGestures.push(action.defaultGesture);
      } else {
        const availableGesture = gestures.find((g: string) => !usedGestures.includes(g));
        if (availableGesture) {
          defaultMapping[action.id] = availableGesture;
          usedGestures.push(availableGesture);
        } else {
          defaultMapping[action.id] = gestures[index % gestures.length];
        }
      }
    });
    
    updateSetting("actionMapping", defaultMapping);
  };

  const handleGestureChange = (actionId: string, newGestureId: string) => {
    const currentMapping = settings.actionMapping || {};
    const currentGestureOfAction = currentMapping[actionId];
    
    // Find if another action is using the new gesture
    const otherActionWithNewGesture = Object.entries(currentMapping).find(
      ([otherActionId, gestureId]) => otherActionId !== actionId && gestureId === newGestureId
    );
    
    let newMapping: Record<string, string>;
    if (otherActionWithNewGesture) {
      // Swap: give the other action our current gesture
      newMapping = {
        ...currentMapping,
        [actionId]: newGestureId,
        [otherActionWithNewGesture[0]]: currentGestureOfAction
      };
    } else {
      newMapping = {
        ...currentMapping,
        [actionId]: newGestureId
      };
    }
    
    updateSetting("actionMapping", newMapping);
    setOpenDropdown(null);
  };

  const toggleDropdown = (actionId: string) => {
    setOpenDropdown(prev => prev === actionId ? null : actionId);
  };

  // Render action mapping config for eye control
  const renderActionMappingConfig = () => {
    const gestures = settings.eyeControlGestures || [];
    const actions = getActionsForGestureCount(gestures.length);
    const modeInfo = getScanModeInfo(gestures.length);
    const gestureOptions = getGestureOptions();
    const actionMapping = settings.actionMapping || {};

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="text-center space-y-2 mb-4">
          <h2 className="text-2xl font-bold">{t("actionMappingTitle")}</h2>
          <p className="text-muted-foreground">
            {isFemale ? t("clickGestureToChangeFemale") : t("clickGestureToChangeMale")}
          </p>
        </div>

        {/* Mapping rows */}
        <div className="space-y-3">
          {actions.map(action => {
            const ActionIcon = getActionIcon(action.id);
            const currentGesture = gestureOptions.find(g => g.id === actionMapping[action.id]);
            const availableGestureOptions = gestureOptions.filter(g => gestures.includes(g.id));
            const isOpen = openDropdown === action.id;

            return (
              <div key={action.id} className="relative">
                <div className={cn(
                  "w-full flex items-center gap-3 p-3 rounded-xl",
                  "bg-card border-2 border-primary/20",
                  "transition-all duration-200"
                )}>
                  {/* Action name - fixed width for alignment */}
                  <span className={cn(
                    "text-sm font-medium w-[110px] text-primary flex-shrink-0 whitespace-nowrap",
                    isRTL ? "text-right" : "text-left"
                  )}>
                    {action.label}
                  </span>
                  
                  {/* Direction icon - fixed width column for vertical alignment */}
                  <div className="w-7 flex items-center justify-center flex-shrink-0">
                    <ActionIcon className="w-4 h-4 text-primary" />
                  </div>
                  
                  {/* Gesture dropdown button */}
                  <div className="flex-1 flex justify-end">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDropdown(action.id);
                      }}
                      className={cn(
                        "flex items-center justify-between gap-2 px-3 py-2 rounded-xl w-36",
                        "bg-primary/20 hover:bg-primary/30 text-foreground font-medium",
                        "transition-all duration-200 border border-primary/30",
                        isOpen && "ring-2 ring-primary bg-primary/30"
                      )}
                    >
                      <ChevronDown className={cn(
                        "w-4 h-4 transition-transform text-primary",
                        isOpen && "rotate-180"
                      )} />
                      <span className="text-right flex-1 text-sm">{currentGesture?.shortLabel || t("selectGesturePrompt")}</span>
                    </button>
                  </div>
                </div>
                
                {/* Dropdown */}
                {isOpen && (
                  <div className="absolute left-4 right-4 top-full mt-1 z-50 bg-card rounded-xl shadow-lg border-2 border-primary/20 overflow-hidden">
                    {availableGestureOptions.map(gesture => (
                      <button
                        key={gesture.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGestureChange(action.id, gesture.id);
                        }}
                        className={cn(
                          "w-full px-3 py-2 text-right text-sm hover:bg-primary/10 transition-colors",
                          actionMapping[action.id] === gesture.id && "bg-primary text-primary-foreground hover:bg-primary"
                        )}
                      >
                        {gesture.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render eye control config
  const renderEyeControlConfig = () => {
    const selectedGestures = settings.eyeControlGestures || [];
    
    return (
      <div className="space-y-4">
        {/* Header - no icon, matching gender selection step */}
        <div className="text-center space-y-2 mb-4">
          <h2 className="text-2xl font-bold">
            {isFemale ? t("selectGesturesFemale") : t("selectGesturesMale")}
          </h2>
          <p className="text-muted-foreground">
            {t("canChangeInSettings")}
          </p>
        </div>

        {/* Gesture Selection in Plus Shape */}
        <div className="flex flex-col items-center gap-2 mb-4">
          {/* Top: Look Up */}
          <GestureButton
            id="look_up"
            icon={eyeGestureIcons.look_up}
            label={t("lookUp")}
            isSelected={selectedGestures.includes("look_up")}
            onToggle={() => toggleEyeGesture("look_up")}
            isRTL={isRTL}
          />
          
          {/* Middle Row - Physical position fixed: left on screen-left, right on screen-right */}
          {/* Use dir="ltr" to prevent RTL from flipping the order */}
          <div className="flex items-center gap-2" dir="ltr">
            <GestureButton
              id="look_left"
              icon={eyeGestureIcons.look_left}
              label={t("lookLeft")}
              isSelected={selectedGestures.includes("look_left")}
              onToggle={() => toggleEyeGesture("look_left")}
              isRTL={isRTL}
            />
            <GestureButton
              id="blink"
              icon={eyeGestureIcons.blink}
              label={t("blink")}
              isSelected={selectedGestures.includes("blink")}
              onToggle={() => toggleEyeGesture("blink")}
              isRTL={isRTL}
            />
            <GestureButton
              id="look_right"
              icon={eyeGestureIcons.look_right}
              label={t("lookRight")}
              isSelected={selectedGestures.includes("look_right")}
              onToggle={() => toggleEyeGesture("look_right")}
              isRTL={isRTL}
            />
          </div>
          
          {/* Bottom: Look Down */}
          <GestureButton
            id="look_down"
            icon={eyeGestureIcons.look_down}
            label={t("lookDown")}
            isSelected={selectedGestures.includes("look_down")}
            onToggle={() => toggleEyeGesture("look_down")}
            isRTL={isRTL}
          />
        </div>

        {/* Scanning settings when gestures are selected */}
        {selectedGestures.length > 0 && (
          <>
            {/* Mode description */}
            <div className="bg-primary/10 border border-primary/30 rounded-xl p-3 text-center">
              <p className="text-sm text-muted-foreground">
                {getGestureCountDescription(selectedGestures.length)}
              </p>
            </div>

            {/* Scan Control Mode - Only for 4+ gestures (matches EyeControlSettings) */}
            {selectedGestures.length >= 4 && (
              <div className="space-y-2">
                <p className={cn("text-sm text-muted-foreground", isRTL ? "text-right" : "text-left")}>
                  {isFemale ? t("selectScanTypeFemale") : t("selectScanTypeMale")}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      const cellAvail = selectedGestures.length === 5 && false;
                      setSettings(prev => ({
                        ...prev,
                        scanControlMode: "automatic",
                        scanMode: !cellAvail && (prev.scanMode || "cell_by_cell") === "cell_by_cell" ? "row_and_column" : prev.scanMode,
                      }));
                    }}
                    className={cn(
                      "p-3 rounded-xl border-2 transition-all text-sm",
                      settings.scanControlMode === "automatic"
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-card border-border hover:border-primary/30"
                    )}
                  >
                    {t("automaticScanning")}
                  </button>
                  <button
                    onClick={() => updateSetting("scanControlMode", "gesture_controlled")}
                    className={cn(
                      "p-3 rounded-xl border-2 transition-all text-sm",
                      settings.scanControlMode === "gesture_controlled"
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-card border-border hover:border-primary/30"
                    )}
                  >
                    {t("gestureScanning")}
                  </button>
                </div>
              </div>
            )}

            {/* Scan Mode - cell_by_cell only available for 5 gestures + gesture_controlled */}
            {(() => {
              const isCellByCellAvailable =
                selectedGestures.length === 5 && settings.scanControlMode === "gesture_controlled";
              return (
                <div className="space-y-2">
                  <p className={cn("text-sm text-muted-foreground", isRTL ? "text-right" : "text-left")}>{t("scanMode")}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => isCellByCellAvailable && updateSetting("scanMode", "cell_by_cell")}
                      disabled={!isCellByCellAvailable}
                      className={cn(
                        "flex flex-col items-center p-3 rounded-xl border-2 transition-all",
                        settings.scanMode === "cell_by_cell" && isCellByCellAvailable
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-card border-border hover:border-primary/30",
                        !isCellByCellAvailable && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      <span className="font-medium text-sm">{t("cellByCellMode")}</span>
                      <span className={cn(
                        "text-xs mt-1",
                        settings.scanMode === "cell_by_cell" ? "text-primary-foreground/70" : "text-muted-foreground"
                      )}>
                        {t("cellByCellDesc")}
                      </span>
                    </button>
                    <button
                      onClick={() => updateSetting("scanMode", "row_and_column")}
                      className={cn(
                        "flex flex-col items-center p-3 rounded-xl border-2 transition-all",
                        settings.scanMode === "row_and_column"
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-card border-border hover:border-primary/30"
                      )}
                    >
                      <span className="font-medium text-sm">{t("rowAndColumnMode")}</span>
                      <span className={cn(
                        "text-xs mt-1",
                        settings.scanMode === "row_and_column" ? "text-primary-foreground/70" : "text-muted-foreground"
                      )}>
                        {t("rowAndColumnDesc")}
                      </span>
                    </button>
                  </div>
                </div>
              );
            })()}

          </>
        )}
      </div>
    );
  };

  // Note: External switch and mouth gestures configs removed
  // Only eye_control is used in simplified onboarding flow

  return (
    <>
      <Helmet>
        <title>ברוכים הבאים - EyeControl App</title>
      </Helmet>

      <div
        className="min-h-screen bg-background flex flex-col items-center justify-center px-6 pt-16 pb-10 overflow-y-auto"
        style={{ minHeight: '100dvh' }}
        dir={dir}
      >
        {/* Caregiver Language Toggle - Bottom on welcome, top-corner on other steps */}
        {currentStep === "welcome" ? (
          <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50">
            <button
              onClick={() => handleCaregiverLanguageChange(caregiverLanguage === "he" ? "en" : "he")}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-full",
                "bg-card/80 backdrop-blur-sm border border-border",
                "hover:bg-card transition-colors text-sm font-medium"
              )}
            >
              <Globe className="w-4 h-4 text-muted-foreground" />
              <span>{caregiverLanguage === "he" ? "English" : "עברית"}</span>
            </button>
          </div>
        ) : currentStep !== "complete" && (
          <button
            onClick={() => handleCaregiverLanguageChange(caregiverLanguage === "he" ? "en" : "he")}
            className={cn(
              "fixed top-6 z-50 flex items-center gap-2 px-3 py-1.5 rounded-full",
              "bg-card/80 backdrop-blur-sm border border-border",
              "hover:bg-card transition-colors text-sm font-medium",
              dir === "rtl" ? "left-6" : "right-6"
            )}
          >
            <Globe className="w-4 h-4 text-muted-foreground" />
            <span>{caregiverLanguage === "he" ? "EN" : "עב"}</span>
          </button>
        )}

        {/* Top mask strip - hides content scrolling behind the fixed header */}
        <div
          className="fixed top-0 left-0 right-0 z-40 bg-background"
          style={{ height: '56px' }}
        />

        {/* Progress */}
        <div className="fixed top-6 left-1/2 -translate-x-1/2 flex gap-2 z-50">
          {STEPS.slice(1, -1).map((step, i) => {
            // Skip action_mapping in progress if less than 2 gestures
            if (step === "action_mapping" && (settings.eyeControlGestures || []).length < 2) {
              return null;
            }
            return (
              <div
                key={step}
                className={cn(
                  "w-2 h-2 rounded-full transition-all",
                  stepIndex > i + 1
                    ? "bg-primary w-6"
                    : stepIndex === i + 1
                    ? "bg-primary"
                    : "bg-muted"
                )}
              />
            );
          })}
        </div>

        {/* Content */}
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait">
            {currentStep === "welcome" && (
              <OnboardingStep key="welcome">
                <div className="text-center space-y-6">
                  <motion.div
                    className="w-24 h-24 mx-auto rounded-2xl bg-primary flex items-center justify-center"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 0.2 }}
                  >
                    <img
                      src={appLogo}
                      alt="EyeControl App"
                      className="w-24 h-24"
                    />
                  </motion.div>
                  <div className="space-y-2">
                    <h1 className="text-3xl font-bold text-foreground">
                      {t("welcomeToAyuni")}
                    </h1>
                    <p className="text-muted-foreground text-lg">
                      {t("aacAppDescription")}
                    </p>
                  </div>
                  <p className="text-muted-foreground">
                    {t("letsSetup")}
                  </p>
                  <Button
                    size="lg"
                    className="w-full text-lg py-6"
                    onClick={goNext}
                  >
                    <Sparkles className="w-5 h-5 ml-2" />
                    {t("letsStart")}
                  </Button>
                </div>
              </OnboardingStep>
            )}

            {currentStep === "name" && (
              <OnboardingStep key="name">
                <div className="space-y-6">
                  <div className="text-center space-y-2">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <User className="w-8 h-8 text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold">{t("whatIsYourName")}</h2>
                    <p className="text-muted-foreground">
                      {t("willUseForPersonal")}
                    </p>
                  </div>

                  <Input
                    value={settings.name || ""}
                    onChange={(e) => updateSetting("name", e.target.value)}
                    placeholder={t("yourName")}
                    className="text-center text-xl py-6 rounded-xl"
                    autoFocus
                  />

                  <NavigationButtons
                    onBack={goBack}
                    onNext={goNext}
                    nextDisabled={!settings.name?.trim()}
                    isRTL={isRTL}
                  />
                </div>
              </OnboardingStep>
            )}

            {currentStep === "gender" && (
              <OnboardingStep key="gender">
                <div className="space-y-6">
                  <div className="text-center space-y-2">
                    <h2 className="text-2xl font-bold">
                      {t("howToAddress")}
                    </h2>
                    <p className="text-muted-foreground">
                      {t("willUseForAddressing")}
                    </p>
                  </div>

                  <div className="grid gap-3">
                    {GENDERS.map((g) => (
                      <button
                        key={g.value}
                        onClick={() => {
                          updateSetting("gender", g.value);
                          // Sync voice gender with user gender
                          if (g.value === "male" || g.value === "female") {
                            updateSetting("voiceGender", g.value);
                          }
                        }}
                        className={cn(
                          "p-4 rounded-2xl border-2 transition-all",
                          isRTL ? "text-right" : "text-left",
                          settings.gender === g.value
                            ? "border-primary bg-primary/10"
                            : "border-border hover:border-primary/50"
                        )}
                      >
                        <div className="font-bold text-lg">{g.label}</div>
                        <div className="text-sm text-muted-foreground">
                          {g.description}
                        </div>
                      </button>
                    ))}
                  </div>

                  <NavigationButtons onBack={goBack} onNext={goNext} isRTL={isRTL} />
                </div>
              </OnboardingStep>
            )}

            {currentStep === "language" && (
              <OnboardingStep key="language">
                <div className="space-y-6">
                  <div className="text-center space-y-2">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Globe className="w-8 h-8 text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold">{isFemale ? t("whichLanguageFemale") : t("whichLanguageMale")}</h2>
                    <p className="text-muted-foreground">
                      {t("canChangeInSettings")}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => updateSetting("language", lang.code)}
                        className={cn(
                          "p-4 rounded-2xl border-2 transition-all flex items-center gap-3",
                          settings.language === lang.code
                            ? "border-primary bg-primary/10"
                            : "border-border hover:border-primary/50"
                        )}
                      >
                        <span className="font-medium">{lang.name}</span>
                      </button>
                    ))}
                  </div>

                  <NavigationButtons onBack={goBack} onNext={goNext} isRTL={isRTL} />
                </div>
              </OnboardingStep>
            )}

            {currentStep === "control_config" && (
              <OnboardingStep key="control_config">
                <div className="space-y-6">
                  {renderEyeControlConfig()}

                  <NavigationButtons 
                    onBack={goBack} 
                    onNext={() => {
                      initializeActionMapping();
                      goNext();
                    }}
                    nextDisabled={!canContinueControlConfig()}
                    isRTL={isRTL}
                  />
                </div>
              </OnboardingStep>
            )}

            {currentStep === "action_mapping" && (
              <OnboardingStep key="action_mapping">
                <div className="space-y-6">
                  {renderActionMappingConfig()}

                  <NavigationButtons 
                    onBack={goBack} 
                    onNext={goNext}
                    isRTL={isRTL}
                  />
                </div>
              </OnboardingStep>
            )}

            {currentStep === "complete" && (
              <OnboardingStep key="complete">
                <div className="text-center space-y-3">
                  <motion.div
                    className="w-14 h-14 rounded-full bg-primary flex items-center justify-center mx-auto"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 0.2 }}
                  >
                    <Check className="w-7 h-7 text-primary-foreground" />
                  </motion.div>

                  <div className="space-y-1">
                    <h2 className="text-2xl font-bold">
                      {settings.gender === "female"
                        ? t("greatReadyFemale")
                        : settings.gender === "male"
                        ? t("greatReadyMale")
                        : t("greatReady")}
                    </h2>
                  </div>

                  <div className="bg-card rounded-2xl border border-border p-4 text-right space-y-2.5">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground text-sm">{t("labelName")}</span>
                      <span className="font-medium text-sm">{settings.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground text-sm">{t("labelAddress")}</span>
                      <span className="font-medium text-sm">
                        {GENDERS.find((g) => g.value === settings.gender)?.label}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground text-sm">{t("labelLanguage")}</span>
                      <span className="font-medium text-sm">
                        {LANGUAGES.find((l) => l.code === settings.language)?.name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground text-sm">{t("labelControlMethod")}</span>
                      <span className="font-medium text-sm">
                        {t(`control_${settings.controlMethod}_title` as any)}
                      </span>
                    </div>
                  </div>

                  <div className="!mt-10">
                    <Button
                      size="lg"
                      className="w-full text-lg py-5"
                      onClick={handleComplete}
                    >
                      <Sparkles className="w-5 h-5 ml-2" />
                      {t("letsStartNeutral")}
                    </Button>
                  </div>

                  <button
                    onClick={goBack}
                    className="text-muted-foreground hover:text-foreground transition-colors text-sm mt-1"
                  >
                    {t("backToEdit")}
                  </button>
                </div>
              </OnboardingStep>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}

// Step wrapper with animation
function OnboardingStep({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  );
}

// Gesture button component for eye control
function GestureButton({ 
  id, 
  icon: Icon,
  label,
  isSelected, 
  onToggle,
  isRTL = true
}: { 
  id: string;
  icon: React.ElementType;
  label: string;
  isSelected: boolean; 
  onToggle: () => void;
  isRTL?: boolean;
}) {
  return (
    <button
      onClick={onToggle}
      dir={isRTL ? "rtl" : "ltr"}
      className={cn(
        "flex items-center gap-3 p-3 rounded-xl transition-all",
        "border-2 cursor-pointer w-[220px]",
        isSelected
          ? "bg-primary/10 border-primary shadow-md"
          : "bg-card border-border hover:border-primary/30"
      )}
    >
      {/* Keep DOM order consistent; `dir` mirrors the layout for RTL/LTR */}
      <div className={cn(
        "w-6 h-6 rounded border-2 flex items-center justify-center transition-all flex-shrink-0",
        isSelected
          ? "bg-primary border-primary"
          : "bg-background border-muted-foreground/40"
      )}>
        {isSelected && (
          <Check className="w-4 h-4 text-primary-foreground stroke-[3]" />
        )}
      </div>

      <div className={cn(
        "w-9 h-9 rounded-full flex items-center justify-center transition-colors flex-shrink-0",
        isSelected ? "bg-slate-800" : "bg-muted"
      )}>
        <Icon className={cn(
          "w-5 h-5 transition-colors",
          isSelected ? "text-accent" : "text-muted-foreground"
        )} />
      </div>

      <span className={cn(
        "text-sm font-medium w-[110px] whitespace-nowrap",
        isRTL ? "text-right" : "text-left",
        isSelected ? "text-primary" : "text-foreground"
      )}>
        {label}
      </span>
    </button>
  );
}

// Navigation buttons
function NavigationButtons({
  onBack,
  onNext,
  nextDisabled = false,
  isRTL = true,
}: {
  onBack: () => void;
  onNext: () => void;
  nextDisabled?: boolean;
  isRTL?: boolean;
}) {
  const { t } = useCaregiverTranslation();
  
  return (
    <div className="flex gap-3 pt-4">
      <Button variant="outline" size="lg" className="flex-1" onClick={onBack}>
        {isRTL ? <ArrowRight className="w-5 h-5 ml-2" /> : <ArrowLeft className="w-5 h-5 mr-2" />}
        {t("previousButton")}
      </Button>
      <Button
        size="lg"
        className="flex-1"
        onClick={onNext}
        disabled={nextDisabled}
      >
        {t("nextButton")}
        {isRTL ? <ArrowLeft className="w-5 h-5 mr-2" /> : <ArrowRight className="w-5 h-5 ml-2" />}
      </Button>
    </div>
  );
}
